import React, { useEffect, useMemo, useRef, useState } from "react";
import { Breadcrum } from "../../components/Layout/breadcrum";
import { ScrollNav } from "../../components/ScrollNav";
import { SearchBox } from "../../components/SearchBox";
import PaginationComponent from "../../components/Pagination";
import { useNavigate } from "react-router-dom";
import CreditNoteData from "./BillingData/creditNoteData";
import RightSideModal from "../../components/CustomModal/customRightModal";
import FilterIcon from "../../assets/Order/FilterIcon.svg";
import { POST } from "../../utils/webService";
import { CN_SELLER_DOWNLOAD, GET_CN_DETAILS_FOR_SELLER } from "../../utils/ApiUrls";
import { toast } from "react-hot-toast";
import { Spinner } from "../../components/Spinner";
import downloadIcon from "../../assets/download.svg";

interface ICreditNoteProps {}

const CreditNote: React.FunctionComponent<ICreditNoteProps> = (props) => {
  const navigate = useNavigate();
  const [renderingComponents, setRenderingComponents] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(true);
  const [appliedStatuses, setAppliedStatuses] = useState<
    Array<"PENDING" | "CLOSED">
  >([]);
  const [draftStatuses, setDraftStatuses] = useState<
    Array<"PENDING" | "CLOSED">
  >([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [cnRows, setCnRows] = useState<any[]>([]);
  const [cnPagination, setCnPagination] = useState<{
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  } | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const didInitialFetch = useRef(false);
  const STATUS_OPTIONS: Array<{ label: string; value: "PENDING" | "CLOSED" }> =
    [
      { label: "Pending", value: "PENDING" },
      { label: "Closed", value: "CLOSED" },
    ];
  const arrayData = [
    { label: "Orders" },
    { label: "Invoice" },
    { label: "Credit Notes" },
    { label: "COD" },
  ];

  const render = (id: any) => {
    if (id === 0) {
      navigate("/billing/orders");
    } else if (id === 1) {
      navigate("/billing/invoices");
    } else if (id === 2) {
      navigate("/billing/credit-notes");
    } else if (id === 3) {
      navigate("/billing/cod");
    }
  };

  //on page change index
  const onPageIndexChange = (paginationData: any) => {
    setCurrentPage(paginationData.currentPage);
  };

  // on per page item change
  const onPerPageItemChange = (paginationData: any) => {
    setItemsPerPage(paginationData.itemsPerPage);
    setCurrentPage(1);
  };

  const setScrollIndex = (id: number) => {
    setRenderingComponents(id);
    render(id);
  };

  const fetchCreditNotes = async (payload?: any) => {
    try {
      setIsLoading(true);
      const reqPayload: any = payload || {};

      // Only send search when user actually typed something.
      // Avoid sending { search: "" } (backend treats it differently in some envs).
      if (typeof reqPayload.search === "undefined") {
        const nextSearch = (search || "").trim();
        if (nextSearch) reqPayload.search = nextSearch;
      } else if (typeof reqPayload.search === "string" && !reqPayload.search.trim()) {
        delete reqPayload.search;
      }

      // CN filter: status
      if (typeof reqPayload.status === "undefined" && appliedStatuses.length > 0) {
        reqPayload.status =
          appliedStatuses.length === 1 ? appliedStatuses[0] : appliedStatuses;
      }

      // Pagination (backend supports page/limit in response)
      if (typeof reqPayload.page === "undefined") reqPayload.page = currentPage;
      if (typeof reqPayload.limit === "undefined") reqPayload.limit = itemsPerPage;

      const resp = await POST(GET_CN_DETAILS_FOR_SELLER, reqPayload);
      const data = resp?.data;

      if (data?.success === false) {
        toast.error(data?.message || "Failed to fetch credit notes");
        setCnRows([]);
        return;
      }

      const results = data?.data ?? [];

      setCnRows(Array.isArray(results) ? results : []);
      setCnPagination(
        data?.pagination && typeof data.pagination === "object"
          ? {
              total: Number(data.pagination.total || 0),
              page: Number(data.pagination.page || 1),
              limit: Number(data.pagination.limit || itemsPerPage),
              totalPages: Number(data.pagination.totalPages || 1),
            }
          : null
      );
    } catch (e: any) {
      toast.error(e?.message || "Failed to fetch credit notes");
      setCnRows([]);
      setCnPagination(null);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadSellerFile = async () => {
    try {
      setIsDownloading(true);

      const filterOne: any[] = [];
      if (appliedStatuses.length > 0) {
        filterOne.push({
          credit_status: {
            $in: appliedStatuses,
          },
        });
      }

      const resp = await POST(CN_SELLER_DOWNLOAD, { filterOne });
      const data = resp?.data;

      if (!data?.success || !data?.url) {
        toast.error(data?.message || "Failed to generate download");
        return;
      }

      const url = String(data.url);
      const fileName =
        typeof data.fileName === "string" && data.fileName.trim()
          ? data.fileName.trim()
          : `credit-notes-${Date.now()}.csv`;

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e: any) {
      toast.error(e?.message || "Failed to download file");
    } finally {
      setIsDownloading(false);
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    if (didInitialFetch.current) return;
    didInitialFetch.current = true;
    fetchCreditNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced fetch on search change
  useEffect(() => {
    const t = window.setTimeout(() => {
      setCurrentPage(1);
      fetchCreditNotes();
    }, 400);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Refetch when pagination changes
  useEffect(() => {
    fetchCreditNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage]);

  // Refetch when applied status changes (e.g. Closed)
  useEffect(() => {
    setCurrentPage(1);
    fetchCreditNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedStatuses]);

  // Backend already filters + paginates; show exactly what API returns.
  const pagedRows = cnRows;
  const totalItemCount = cnPagination?.total ?? cnRows.length;

  const openFilter = () => {
    // Open with whatever the user previously applied (can be empty).
    // Empty selection means "no filter applied".
    setDraftStatuses(appliedStatuses);
    setIsFilterOpen(true);
  };

  const toggleDraftStatus = (status: "PENDING" | "CLOSED") => {
    setDraftStatuses((prev) => {
      const has = prev.includes(status);
      if (has) return prev.filter((s) => s !== status);
      return [...prev, status];
    });
  };

  const resetAll = () => {
    // Clear selections (no filter), close modal, and refetch unfiltered list
    setDraftStatuses([]);
    setAppliedStatuses([]);
    setSearch("");
    setCurrentPage(1);
    setIsFilterOpen(false);
  };

  const applyFilters = () => {
    // If user deselects everything, treat as "no filter" (show all)
    const next = draftStatuses.length === 0 ? [] : draftStatuses;
    setAppliedStatuses(next);
    setCurrentPage(1);
    setIsFilterOpen(false);
  };

  const filterCount =
    appliedStatuses.length === 0
      ? 0
      : STATUS_OPTIONS.filter((s) => appliedStatuses.includes(s.value)).length;
  return (
    <>
      <div>
        <Breadcrum label="Billing" />
        <div className="customScroll">
          <div className="lg:flex justify-between mx-4 lg:mt-2 lg:mb-4">
            <div>
              <ScrollNav
                arrayData={arrayData}
                showNumber={false}
                setScrollIndex={setScrollIndex}
                defaultIndexValue={2}
              />
            </div>
            <div className="flex items-center gap-3">
              <div className="min-w-[220px]">
                <SearchBox
                  label="Search"
                  customPlaceholder="Search by Credit Note No, Invoice No, Transaction ID..."
                  value={search}
                  onChange={(e: any) => setSearch(e?.target?.value || "")}
                />
              </div>

              <button
                type="button"
                onClick={downloadSellerFile}
                disabled={isDownloading}
                className={`flex items-center gap-2 px-4 h-[36px] rounded-full bg-white text-[#1C1C1C] border border-[#1C1C1C] font-Open text-sm font-semibold whitespace-nowrap ${
                  isDownloading ? "opacity-70 cursor-not-allowed" : "hover:bg-gray-50"
                }`}
              >
                <img src={downloadIcon} alt="" className="w-4 h-4" />
                {isDownloading ? "Downloading..." : "Download File"}
              </button>

              <div
                onClick={openFilter}
                className="text-center gap-1 items-center flex p-2 rounded-md md:text-[14px] font-Open font-semibold leading-5 whitespace-nowrap text-blue-600 hover:bg-blue-50 cursor-pointer"
              >
                <img src={FilterIcon} alt="Filter" />
                FILTER
                {filterCount > 0 && (
                  <p className="border border-blue-600 px-1 text-xs text-center items-center flex rounded-full">
                    {filterCount}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="mx-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-[400px]">
                <Spinner />
              </div>
            ) : (
              <CreditNoteData creditData={pagedRows} />
            )}
          </div>
        </div>

        {totalItemCount > 0 && (
          <PaginationComponent
            totalItems={totalItemCount}
            itemsPerPageOptions={[
                      10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000,
                    ]}
            onPageChange={onPageIndexChange}
            onItemsPerPageChange={onPerPageItemChange}
          />
        )}
      </div>

      <RightSideModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        wrapperClassName="!w-[420px] max-w-full"
      >
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 ">
            <p className="text-xl font-semibold font-Open text-[#1C1C1C]">
              Filter
            </p>
            <button
              type="button"
              onClick={() => setIsFilterOpen(false)}
              className="p-2 rounded hover:bg-gray-100"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5 text-[#1C1C1C]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-auto p-5">
            <div className="border border-[#E8E8E8] rounded-lg overflow-hidden ">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 bg-[#F6F6F6]"
                onClick={() => setStatusOpen((v) => !v)}
              >
                <p className="font-Open text-sm font-semibold text-[#1C1C1C] ">
                  Status
                </p>
                <svg
                  className={`w-4 h-4 transform transition-transform ${
                    statusOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {statusOpen && (
                <div className="border-t border-[#E8E8E8]">
                  <div className="px-4 py-3 flex items-center gap-3 text-sm font-Open text-[#1C1C1C]">
                    <input
                      type="checkbox"
                      checked={draftStatuses.includes("PENDING")}
                      onChange={() => toggleDraftStatus("PENDING")}
                      className="!h-4 !w-4 !p-0 !m-0 accent-[#1C1C1C]"
                    />
                    <span>Pending</span>
                  </div>
                  <div className="px-4 py-3 flex items-center gap-3 text-sm font-Open text-[#1C1C1C] border-t border-[#E8E8E8]">
                    <input
                      type="checkbox"
                      checked={draftStatuses.includes("CLOSED")}
                      onChange={() => toggleDraftStatus("CLOSED")}
                      className="!h-4 !w-4 !p-0 !m-0 accent-[#1C1C1C]"
                    />
                    <span>Closed</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-[#E8E8E8] px-5 py-4 flex justify-end gap-3 bg-white rounded-t-2xl shadow-[0_-4px_8px_0_rgba(0,0,0,0.04)]">
            <button
              type="button"
              onClick={resetAll}
              className="px-5 py-2 rounded-full border border-[#AFAFAF] text-[#1C1C1C] font-Open text-sm"
            >
              Reset All
            </button>
            <button
              type="button"
              onClick={applyFilters}
              className="px-5 py-2 rounded-full bg-[#1C1C1C] text-white font-Open text-sm"
            >
              Apply
            </button>
          </div>
        </div>
      </RightSideModal>
    </>
  );
};

export default CreditNote;
